import type { ReactNode } from "react"

import type { LucideIcon } from "@/components/icons"

import { ChevronDown } from "@/components/icons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { cn } from "@/lib/utils"

type CollapsibleDashboardCardProps = {
  children: ReactNode
  headerActions?: ReactNode
  icon: LucideIcon
  id?: string
  /** Controlled open state — owned by the page so it can also gate the query. */
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
}

/**
 * Controlled collapsible card matching {@link CollapsibleManagementSection}'s
 * look, but with open state lifted to the parent so the parent can gate the
 * section's data fetch on `open`. Children are only rendered while open, so a
 * collapsed section mounts nothing and triggers no query.
 */
export function CollapsibleDashboardCard({
  children,
  headerActions,
  icon: Icon,
  id,
  open,
  onOpenChange,
  title,
}: CollapsibleDashboardCardProps) {
  return (
    <Collapsible asChild open={open} onOpenChange={onOpenChange}>
      <Card className="min-w-0 scroll-mt-24 self-start rounded-lg shadow-sm" id={id}>
        <CardHeader className="pb-0">
          {/* headerActions are a sibling of the trigger button, never a child —
              nesting an interactive element inside a <button> is invalid HTML
              and triggers a React hydration error. */}
          <div className="-mx-6 -mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className={cn(
                  "group/section flex min-h-11 flex-1 items-center justify-between rounded-t-lg px-6 py-4 text-left transition-colors duration-150",
                  "hover:bg-muted/50",
                  "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                )}
              >
                <CardTitle className="flex items-center gap-2">
                  <Icon aria-hidden="true" className="size-5 shrink-0" />
                  {title}
                </CardTitle>
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    "size-5 shrink-0 transition-transform duration-150",
                    open && "rotate-180"
                  )}
                />
              </button>
            </CollapsibleTrigger>
            {headerActions && (
              <div className="flex min-h-11 flex-wrap items-center gap-2 px-6 pb-3 sm:pb-0">
                {headerActions}
              </div>
            )}
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-4">{open ? children : null}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
